import { useMemo } from 'react';

import { useTypedSelector } from '../../../hooks/use-typedselector';

import {
  type Column,
  type ColumnOption,
  type DiscoveryFileData,
  type FileImportAttribute,
  type Row,
} from './types';

/**
 * Custom hook to transform the backend data into a format suitable for the preview table.
 * Handles empty data, column mapping, and error detection.
 */
function useDiscoveryFileData(): DiscoveryFileData {
  const discoveryData = useTypedSelector(
    (state) => state.discoveryFileUpload.data[0]
  );

  const {
    fileImportAttributes = [],
    fileImportErrors = {},
    firstLine = '',
    rows: rawRows = [],
  } = discoveryData;

  // Building Column Options
  const columnOptions = useMemo((): ColumnOption[] => {
    const options = fileImportAttributes.map((attr: FileImportAttribute) => ({
      label: attr.name,
      value: attr.name,
      isMandatory: attr.isMandatory,
      isCritical: false,
    }));

    return options.sort((a, b) => {
      const aDisabled = !a.label;
      const bDisabled = !b.label;

      if (aDisabled && !bDisabled) {
        return 1;
      }

      if (!aDisabled && bDisabled) {
        return -1;
      }

      return a.label.localeCompare(b.label);
    });
  }, [fileImportAttributes]);

  // Parse column names from the first line
  const columnNames = useMemo(
    () => firstLine.split(',').map((s: string) => s.trim()),
    [firstLine]
  );

  // Building Columns
  const columns = useMemo(
    (): Column[] =>
      columnNames.map((colName: string, idx: number) => {
        const errorString = fileImportErrors[String(idx + 1)] || '';
        const errors = errorString.split(',').map((e) => e.trim());

        const key = colName || `Unnamed Column ${idx}`;

        return {
          key,
          label: colName || null,
          value: key,
          positionKey: `col-${idx}`,
          isCritical: errors.includes('null') || errors.includes('header'),
          isDateError: errors.includes('date'),
          index: idx,
        };
      }),
    [columnNames, fileImportErrors]
  );

  // Building Rows
  const rows = useMemo((): Row[] => {
    if (!rawRows.length) {
      return [];
    }

    const [, ...dataLines] = rawRows;

    return dataLines.map((line: string) => {
      const values = line.split(',').map((v: string) => v.trim());
      const rowObj: Record<string, string> = { AssetID: '' };

      columnNames.forEach((header: string, idx: number) => {
        const key = header || `Unnamed Column ${idx}`;

        rowObj[key] = values[idx] ?? '';
      });

      if (rowObj.appname) {
        rowObj.app = rowObj.appname;
      }

      return rowObj as Row;
    });
  }, [rawRows, columnNames]);

  return { columnOptions, columns, rows };
}

export default useDiscoveryFileData;
